import { apiClient } from './apiClient';

export interface RequestPasswordResetRequest {
  email: string;
}

export interface RequestPasswordResetResponse {
  message: string;
}

export interface VerifyResetTokenResponse {
  valid: boolean;
  email?: string;
}

export interface ResetPasswordRequest {
  token: string;
  newPassword: string;
}

export interface ResetPasswordResponse {
  message: string;
}

export async function requestPasswordReset(
  request: RequestPasswordResetRequest
): Promise<RequestPasswordResetResponse> {
  const response = await apiClient.post('/api/auth/password-reset/request', request);
  return { message: response.data.message };
}

export async function verifyResetToken(token: string): Promise<VerifyResetTokenResponse> {
  const response = await apiClient.get(`/api/auth/password-reset/verify/${token}`);
  const data = response.data;
  if (!data.valid) {
    throw new Error(data.message || 'Invalid or expired reset token');
  }
  return { valid: data.valid, email: data.email };
}

export async function resetPassword(request: ResetPasswordRequest): Promise<ResetPasswordResponse> {
  const response = await apiClient.post('/api/auth/password-reset/reset', request);
  return { message: response.data.message };
}

// Email Verification API

export interface VerifyEmailResponse {
  success: boolean;
  message: string;
  alreadyVerified?: boolean;
}

export interface ResendVerificationRequest {
  email: string;
}

export interface ResendVerificationResponse {
  success: boolean;
  message: string;
}

export interface SendVerificationResponse {
  success: boolean;
  message: string;
}

export async function verifyEmail(token: string): Promise<VerifyEmailResponse> {
  const response = await apiClient.get(`/api/auth/email-verification/verify/${token}`);
  const data = response.data;
  return {
    success: data.success,
    message: data.message,
    alreadyVerified: data.alreadyVerified,
  };
}

export async function resendVerificationEmail(
  request: ResendVerificationRequest
): Promise<ResendVerificationResponse> {
  const response = await apiClient.post('/api/auth/email-verification/resend', request);
  const data = response.data;
  return { success: data.success, message: data.message };
}

export async function sendVerificationEmail(): Promise<SendVerificationResponse> {
  const response = await apiClient.post('/api/auth/email-verification/send', {});
  const data = response.data;
  return { success: data.success, message: data.message };
}

// Session Management API

export interface Session {
  id: string;
  deviceName: string;
  deviceType: string;
  browser: string;
  os: string;
  location: string;
  ipAddress: string;
  lastActivityAt: string;
  createdAt: string;
  isCurrent: boolean;
}

export interface GetSessionsResponse {
  sessions: Session[];
  totalSessions: number;
}

export async function getActiveSessions(): Promise<GetSessionsResponse> {
  const response = await apiClient.get('/api/sessions');
  return response.data.data;
}

export async function terminateSession(sessionId: string): Promise<void> {
  await apiClient.delete(`/api/sessions/${sessionId}`);
}

export async function logoutAllOtherDevices(): Promise<{ sessionsTerminated: number }> {
  const response = await apiClient.post('/api/sessions/logout-all-others', {});
  return response.data.data;
}
